import React, { useState, useEffect } from 'react';
import {
  Modal,
  Form,
  Input,
  Select,
  Button,
  Card,
  Row,
  Col,
  Space,
  Typography,
  Divider,
  Tag,
  message,
  Spin,
  Checkbox,
  Radio,
  List,
  Tooltip,
  Alert
} from 'antd';
import {
  PlusOutlined,
  DeleteOutlined,
  InfoCircleOutlined,
  CheckCircleOutlined,
  ClockCircleOutlined,
  AimOutlined,
  MedicineBoxOutlined,
  HeartOutlined,
  ThunderboltOutlined,
  RestOutlined,
  SmileOutlined
} from '@ant-design/icons';
import moment from 'moment';
import axios from 'axios';
import { ORGAN_SYSTEM_DISPLAY_NAMES } from '../utils/organSystemConstants';
import MomentDatePicker from './MomentDatePicker.tsx';
import { validateSmartGoalData, validatePatientGoalData, validateActionData, sanitizeTextInput, validateMultiplePatientGoals } from '../utils/smartGoalValidation';
import { showErrorMessage, handleValidationErrors, retryApiCall, handleOfflineError } from '../utils/smartGoalErrorHandling';

const { TextArea } = Input;
const { Option } = Select;
const { Title, Text } = Typography;

const PILLAR_OPTIONS = [
  { value: 'Medication', label: 'Medication', icon: <MedicineBoxOutlined />, color: '#1890ff' },
  { value: 'Nutrition', label: 'Nutrition', icon: <HeartOutlined />, color: '#52c41a' },
  { value: 'Movement', label: 'Movement', icon: <ThunderboltOutlined />, color: '#fa8c16' },
  { value: 'Recovery', label: 'Recovery', icon: <RestOutlined />, color: '#722ed1' },
  { value: 'Emotion', label: 'Emotion', icon: <SmileOutlined />, color: '#eb2f96' }
];

const TIER_OPTIONS = [
  { value: 1, label: 'Tier 1 - Easy (Beginner)', description: 'Simple, achievable goals for new patients', color: '#52c41a' },
  { value: 2, label: 'Tier 2 - Moderate', description: 'Standard goals requiring some effort', color: '#1890ff' },
  { value: 3, label: 'Tier 3 - Challenging', description: 'Goals requiring significant commitment', color: '#fa8c16' },
  { value: 4, label: 'Tier 4 - Difficult (Advanced)', description: 'Complex goals for motivated patients', color: '#f5222d' }
];

const EnhancedSmartGoalModal = ({
  visible,
  onCancel,
  onSuccess,
  patientId,
  selectedProblem = null,
  problems = []
}) => {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [patientGoals, setPatientGoals] = useState([]);
  const [activeGoalIndex, setActiveGoalIndex] = useState(0);

  // Initialize with one empty patient goal
  const createEmptyPatientGoal = () => ({
    smartGoal: '',
    relatedPillar: '',
    tier: 1,
    timeline: {
      startDate: moment(),
      targetDate: moment().add(3, 'months'),
      nextReviewDate: moment().add(1, 'month')
    },
    actions: [],
    newActionText: ''
  });

  // Reset form when modal opens/closes
  useEffect(() => {
    if (visible) {
      form.resetFields();
      setPatientGoals([createEmptyPatientGoal()]);
      setActiveGoalIndex(0);

      // Pre-populate problem if selected
      if (selectedProblem) {
        form.setFieldsValue({
          problem: selectedProblem._id,
          system: selectedProblem.organSystems?.[0] || ''
        });
      }
    }
  }, [visible, selectedProblem, form]);

  // Handle problem selection change
  const handleProblemChange = (problemId) => {
    const problem = problems.find(p => p._id === problemId);
    if (problem && problem.organSystems && problem.organSystems.length > 0) {
      form.setFieldsValue({ system: problem.organSystems[0] });
    }
  };

  // Add new patient goal
  const addPatientGoal = () => {
    const newGoal = createEmptyPatientGoal();
    setPatientGoals([...patientGoals, newGoal]);
    setActiveGoalIndex(patientGoals.length);
  };

  // Remove patient goal
  const removePatientGoal = (index) => {
    if (patientGoals.length > 1) {
      const newGoals = patientGoals.filter((_, i) => i !== index);
      setPatientGoals(newGoals);

      // Adjust active index if necessary
      if (activeGoalIndex >= newGoals.length) {
        setActiveGoalIndex(newGoals.length - 1);
      } else if (activeGoalIndex > index) {
        setActiveGoalIndex(activeGoalIndex - 1);
      }
    }
  };

  // Update patient goal field
  const updatePatientGoal = (index, field, value) => {
    const newGoals = [...patientGoals];
    if (field.includes('.')) {
      // Handle nested fields like timeline.startDate
      const [parent, child] = field.split('.');
      newGoals[index][parent][child] = value;
    } else {
      newGoals[index][field] = value;
    }
    setPatientGoals(newGoals);
  };

  // Add action to specific patient goal
  const addActionToGoal = (goalIndex) => {
    const goal = patientGoals[goalIndex];
    if (goal.newActionText && goal.newActionText.trim()) {
      const newGoals = [...patientGoals];
      newGoals[goalIndex].actions.push({
        description: goal.newActionText.trim(),
        completed: false
      });
      newGoals[goalIndex].newActionText = '';
      setPatientGoals(newGoals);
    }
  };

  // Remove action from specific patient goal
  const removeActionFromGoal = (goalIndex, actionIndex) => {
    const newGoals = [...patientGoals];
    newGoals[goalIndex].actions = newGoals[goalIndex].actions.filter((_, i) => i !== actionIndex);
    setPatientGoals(newGoals);
  };

  // Handle form submission
  const handleSubmit = async () => {
    try {
      setLoading(true);

      // Check network connectivity
      if (handleOfflineError('create SMART goal')) {
        setLoading(false);
        return;
      }

      let values;
      try {
        values = await form.validateFields();
      } catch (validationError) {
        console.error('Form validation failed:', validationError);

        // Handle specific field validation errors
        if (validationError.errorFields && validationError.errorFields.length > 0) {
          const fieldErrors = validationError.errorFields.map(field =>
            `${field.name.join('.')}: ${field.errors.join(', ')}`
          ).join('; ');
          message.error(`Please fix the following errors: ${fieldErrors}`);
        } else {
          message.error('Please fill in all required fields');
        }
        setLoading(false);
        return;
      }

      console.log('Form values received:', values);

      // Additional validation for required fields
      if (!values.mdSmartGoalOrder || !values.mdSmartGoalOrder.trim()) {
        console.error('mdSmartGoalOrder field is missing or empty:', values.mdSmartGoalOrder);
        message.error('MD SMART Goal Order is required');
        setLoading(false);
        return;
      }

      if (!values.problem) {
        message.error('Please select a problem from the problem list');
        setLoading(false);
        return;
      }

      // Validate patient goals
      if (patientGoals.length === 0) {
        message.error('At least one patient goal is required');
        setLoading(false);
        return;
      }

      // Validate each patient goal
      const patientGoalValidation = validateMultiplePatientGoals(patientGoals);
      if (!patientGoalValidation.isValid) {
        message.error(`Patient goal validation failed: ${patientGoalValidation.errors.join(', ')}`);
        setLoading(false);
        return;
      }

      // Sanitize text inputs
      const sanitizedValues = {
        ...values,
        mdSmartGoalOrder: sanitizeTextInput(values.mdSmartGoalOrder),
        clientFeedback: values.clientFeedback ? sanitizeTextInput(values.clientFeedback) : '',
        hmReviewNote: values.hmReviewNote ? sanitizeTextInput(values.hmReviewNote) : ''
      };

      // Process and sanitize patient goals
      const processedPatientGoals = patientGoals.map((goal, index) => ({
        smartGoal: sanitizeTextInput(goal.smartGoal),
        relatedPillar: goal.relatedPillar,
        tier: parseInt(goal.tier),
        timeline: {
          startDate: goal.timeline.startDate ? goal.timeline.startDate.toISOString() : moment().toISOString(),
          targetDate: goal.timeline.targetDate ? goal.timeline.targetDate.toISOString() : moment().add(3, 'months').toISOString(),
          nextReviewDate: goal.timeline.nextReviewDate ? goal.timeline.nextReviewDate.toISOString() : moment().add(1, 'month').toISOString()
        },
        actions: goal.actions.map(action => ({
          description: sanitizeTextInput(action.description),
          notes: action.notes ? sanitizeTextInput(action.notes) : '',
          completed: false
        })),
        status: 'Not Started',
        completionRate: 0
      }));

      // Construct the enhanced multi-goal SMART goal payload
      const payload = {
        problem: sanitizedValues.problem,
        system: sanitizedValues.system,
        mdSmartGoalOrder: sanitizedValues.mdSmartGoalOrder,
        patientGoals: processedPatientGoals,
        status: 'Not Started',
        overallCompletionRate: 0,
        clientFeedback: sanitizedValues.clientFeedback,
        hmReviewNote: sanitizedValues.hmReviewNote,
        
        // Maintain compatibility with existing SMART structure
        specific: {
          objective: values.smartGoal,
          description: values.mdSmartGoalOrder
        },
        measurable: {
          metrics: [],
          qualitativeOutcomes: []
        },
        achievable: {
          feasibilityAssessment: `Tier ${values.tier} goal designed for appropriate difficulty level`,
          barriers: [],
          resources: []
        },
        relevant: {
          connectionToProblem: `Addresses ${values.system || 'clinical'} aspects of the selected problem`,
          patientPriority: 'Medium',
          clinicalPriority: 'Medium'
        },
        timeBound: {
          startDate: moment().toISOString(),
          targetDate: moment().add(3, 'months').toISOString(),
          milestones: [],
          reviewFrequency: 'Monthly'
        },
        category: 'Clinical',
        tags: values.system ? [values.system] : [],
        notes: sanitizedValues.hmReviewNote
      };

      // Final validation of complete payload
      const validation = validateSmartGoalData(payload);
      if (!validation.isValid) {
        console.error('SMART Goal validation failed:', validation.errors);
        message.error(`Validation failed: ${validation.errors.join(', ')}`);
        setLoading(false);
        return;
      }

      console.log('Submitting enhanced SMART goal payload:', JSON.stringify(payload, null, 2));

      // Use retry mechanism for API call
      const response = await retryApiCall(
        () => axios.post(`/patients/${patientId}/smart-goals`, payload),
        3,
        1000
      );

      console.log('Enhanced SMART goal created successfully:', response.data);

      message.success('SMART goal created successfully!');
      onSuccess();
      onCancel();
    } catch (error) {
      showErrorMessage(error, 'create SMART goal');
    } finally {
      setLoading(false);
    }
  };

  // Render individual patient goal form
  const renderPatientGoalForm = (goal, index) => (
    <div>
      {/* Patient Goal Text */}
      <div style={{ marginBottom: 16 }}>
        <label style={{ display: 'block', marginBottom: 4, fontWeight: 500 }}>
          SMART Goal Text <span style={{ color: '#ff4d4f' }}>*</span>
        </label>
        <TextArea
          rows={3}
          placeholder="Enter specific, measurable patient goal..."
          maxLength={1000}
          showCount
          value={goal.smartGoal}
          onChange={(e) => updatePatientGoal(index, 'smartGoal', e.target.value)}
        />
      </div>

      {/* Pillar and Tier Row */}
      <Row gutter={16} style={{ marginBottom: 16 }}>
        <Col span={12}>
          <label style={{ display: 'block', marginBottom: 4, fontWeight: 500 }}>
            Related Pillar <span style={{ color: '#ff4d4f' }}>*</span>
          </label>
          <Select
            placeholder="Select pillar"
            value={goal.relatedPillar}
            onChange={(value) => updatePatientGoal(index, 'relatedPillar', value)}
            style={{ width: '100%' }}
          >
            {PILLAR_OPTIONS.map(pillar => (
              <Option key={pillar.value} value={pillar.value}>
                <Space>
                  <span style={{ color: pillar.color }}>{pillar.icon}</span>
                  {pillar.label}
                </Space>
              </Option>
            ))}
          </Select>
        </Col>
        <Col span={12}>
          <label style={{ display: 'block', marginBottom: 4, fontWeight: 500 }}>
            Difficulty Tier <span style={{ color: '#ff4d4f' }}>*</span>
          </label>
          <Select
            placeholder="Select tier"
            value={goal.tier}
            onChange={(value) => updatePatientGoal(index, 'tier', value)}
            style={{ width: '100%' }}
          >
            <Option value={1}>Tier 1 - Easy (Beginner)</Option>
            <Option value={2}>Tier 2 - Moderate</Option>
            <Option value={3}>Tier 3 - Challenging</Option>
            <Option value={4}>Tier 4 - Difficult (Advanced)</Option>
          </Select>
        </Col>
      </Row>

      {/* Timeline Row */}
      <Row gutter={16} style={{ marginBottom: 16 }}>
        <Col span={8}>
          <label style={{ display: 'block', marginBottom: 4, fontWeight: 500 }}>
            Start Date <span style={{ color: '#ff4d4f' }}>*</span>
          </label>
          <MomentDatePicker
            value={goal.timeline.startDate}
            onChange={(date) => updatePatientGoal(index, 'timeline.startDate', date)}
            style={{ width: '100%' }}
          />
        </Col>
        <Col span={8}>
          <label style={{ display: 'block', marginBottom: 4, fontWeight: 500 }}>
            Target Date <span style={{ color: '#ff4d4f' }}>*</span>
          </label>
          <MomentDatePicker
            value={goal.timeline.targetDate}
            onChange={(date) => updatePatientGoal(index, 'timeline.targetDate', date)}
            style={{ width: '100%' }}
          />
        </Col>
        <Col span={8}>
          <label style={{ display: 'block', marginBottom: 4, fontWeight: 500 }}>
            Next Review <span style={{ color: '#ff4d4f' }}>*</span>
          </label>
          <MomentDatePicker
            value={goal.timeline.nextReviewDate}
            onChange={(date) => updatePatientGoal(index, 'timeline.nextReviewDate', date)}
            style={{ width: '100%' }}
          />
        </Col>
      </Row>

      {/* Actions Section */}
      <div>
        <label style={{ display: 'block', marginBottom: 8, fontWeight: 500 }}>Action Steps</label>

        {/* Add Action Input */}
        <div style={{ display: 'flex', marginBottom: 8 }}>
          <Input
            placeholder="Enter action step..."
            value={goal.newActionText}
            onChange={(e) => updatePatientGoal(index, 'newActionText', e.target.value)}
            onPressEnter={() => addActionToGoal(index)}
            style={{ marginRight: 8 }}
          />
          <Button
            type="primary"
            icon={<PlusOutlined />}
            onClick={() => addActionToGoal(index)}
          >
            Add
          </Button>
        </div>

        {/* Actions List */}
        {goal.actions.length > 0 && (
          <List
            size="small"
            dataSource={goal.actions}
            renderItem={(action, actionIndex) => (
              <List.Item
                actions={[
                  <Button
                    type="text"
                    danger
                    icon={<DeleteOutlined />}
                    onClick={() => removeActionFromGoal(index, actionIndex)}
                    size="small"
                  />
                ]}
              >
                <Text>{action.description}</Text>
              </List.Item>
            )}
          />
        )}
      </div>
    </div>
  );

  return (
    <Modal
      title={
        <Space>
          <AimOutlined />
          Create Enhanced SMART Goal
        </Space>
      }
      open={visible}
      onCancel={onCancel}
      width={1000}
      footer={[
        <Button key="cancel" onClick={onCancel}>
          Cancel
        </Button>,
        <Button key="submit" type="primary" loading={loading} onClick={handleSubmit}>
          Create SMART Goal
        </Button>
      ]}
      destroyOnClose
      className="enhanced-smart-goal-modal"
    >
      <Spin spinning={loading}>
        <Form form={form} layout="vertical" requiredMark={false}>
          {/* Problem Selection and System Classification */}
          <Card title="Problem & System Classification" size="small" style={{ marginBottom: 16 }}>
            <Row gutter={16}>
              <Col span={12}>
                <Form.Item
                  name="problem"
                  label="Problem from Problem List"
                  rules={[{ required: true, message: 'Please select a problem' }]}
                >
                  <Select
                    placeholder="Select a problem"
                    onChange={handleProblemChange}
                    showSearch
                    filterOption={(input, option) =>
                      option.children.toLowerCase().indexOf(input.toLowerCase()) >= 0
                    }
                  >
                    {problems.map(problem => (
                      <Option key={problem._id} value={problem._id}>
                        {problem.problemName}
                        {problem.icdCode && <Text type="secondary"> ({problem.icdCode})</Text>}
                      </Option>
                    ))}
                  </Select>
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item
                  name="system"
                  label="Organ System"
                  rules={[{ required: true, message: 'Please select an organ system' }]}
                >
                  <Select placeholder="Select organ system">
                    {Object.entries(ORGAN_SYSTEM_DISPLAY_NAMES).map(([key, value]) => (
                      <Option key={key} value={key}>
                        {value}
                      </Option>
                    ))}
                  </Select>
                </Form.Item>
              </Col>
            </Row>
          </Card>

          {/* MD Clinical Objective */}
          <Card title="MD Clinical Objective" size="small" style={{ marginBottom: 16 }}>
            <Form.Item
              name="mdSmartGoalOrder"
              label="MD SMART Goal Order"
              rules={[{ required: true, message: 'Please enter the MD clinical objective' }]}
              extra="High-level clinical objective set by physician (e.g., 'Reduce blood pressure to <130/80mmHg within 3 months')"
            >
              <TextArea
                rows={2}
                placeholder="Enter the physician's clinical objective..."
                maxLength={500}
                showCount
              />
            </Form.Item>
          </Card>

          {/* Multiple Patient Goals */}
          <Card
            title={
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <span>Patient-Facing SMART Goals ({patientGoals.length})</span>
                <Button
                  type="dashed"
                  icon={<PlusOutlined />}
                  onClick={addPatientGoal}
                  size="small"
                >
                  Add Goal
                </Button>
              </div>
            }
            size="small"
            style={{ marginBottom: 16 }}
          >
            {patientGoals.map((goal, index) => (
              <Card
                key={index}
                type="inner"
                size="small"
                style={{
                  marginBottom: index < patientGoals.length - 1 ? 16 : 0,
                  border: activeGoalIndex === index ? '2px solid #1890ff' : '1px solid #d9d9d9',
                  cursor: 'pointer'
                }}
                title={
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <span>Patient Goal {index + 1}</span>
                    <Space>
                      {goal.relatedPillar && (
                        <Tag color={PILLAR_OPTIONS.find(p => p.value === goal.relatedPillar)?.color}>
                          {PILLAR_OPTIONS.find(p => p.value === goal.relatedPillar)?.icon} {goal.relatedPillar}
                        </Tag>
                      )}
                      {patientGoals.length > 1 && (
                        <Button
                          type="text"
                          danger
                          icon={<DeleteOutlined />}
                          onClick={() => removePatientGoal(index)}
                          size="small"
                        />
                      )}
                    </Space>
                  </div>
                }
                onClick={() => setActiveGoalIndex(index)}
              >
                {renderPatientGoalForm(goal, index)}
              </Card>
            ))}
          </Card>

          {/* Optional Notes */}
          <Card title="Additional Information" size="small" style={{ marginBottom: 16 }}>
            <Row gutter={16}>
              <Col xs={24} sm={12}>
                <Form.Item
                  name="clientFeedback"
                  label="Initial Client Input"
                  extra="Any initial feedback or preferences from the patient"
                >
                  <TextArea
                    rows={3}
                    placeholder="Enter any initial patient feedback..."
                    maxLength={1000}
                    showCount
                  />
                </Form.Item>
              </Col>
              <Col xs={24} sm={12}>
                <Form.Item
                  name="hmReviewNote"
                  label="Health Manager Notes"
                  extra="Initial assessment and notes from health manager"
                >
                  <TextArea
                    rows={3}
                    placeholder="Enter health manager notes..."
                    maxLength={1000}
                    showCount
                  />
                </Form.Item>
              </Col>
            </Row>
          </Card>
        </Form>
      </Spin>
    </Modal>
  );
};

export default EnhancedSmartGoalModal;
